import React, { ReactNode, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../redux/store";
import { getCurrentUser, resetAuthState } from "../../redux/slices/authSlice";
import { fetchMaintenanceStatus } from "../../redux/slices/maintenanceSlice";
import { ProgressSpinner } from "primereact/progressspinner";

interface AuthRedirectProps {
  children: ReactNode;
}

const AuthRedirect: React.FC<AuthRedirectProps> = ({ children }) => {
  const { currentUser, loading } = useSelector(
    (state: RootState) => state.auth
  );
  const { isInMaintenance } = useSelector(
    (state: RootState) => state.maintenance
  );
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const sessionExpired = sessionStorage.getItem("session_expired") === "true";


  useEffect(() => {
    dispatch(fetchMaintenanceStatus());
  }, [dispatch]);

  useEffect(() => {
    if (!currentUser) {
      dispatch(getCurrentUser())
        .unwrap()
        .catch((error) => {
          if (error?.status === 401) {
            dispatch(resetAuthState());
             sessionStorage.removeItem("session_expired");
            if (location.pathname !== "/auth") {
              navigate("/auth", { replace: true });
            }
          } else {
            console.error("Failed to fetch user:", error);
          }
        })
        .finally(() => setIsChecking(false));
    } else {
      setIsChecking(false);
    }
  }, [currentUser, dispatch, location.pathname, navigate]);
  
  useEffect(() => {
    if (!loading && currentUser && !sessionExpired) {
      // Redirect CUSTOMER to maintenance page if maintenance mode is active
      if (isInMaintenance && currentUser.role.role_code === "CUSTOMER") {
        navigate("/maintenance", { replace: true });
        return;
      }

      // Redirect based on role
      switch (currentUser.role.role_code) {
        case "ENTERPRISE":
        case "CUSTOMER":
          navigate("/dashboard", { replace: true });
          break;
        default:
          navigate("/admin", { replace: true });
          break;
      }
    }
  }, [currentUser, loading, navigate, sessionExpired, isInMaintenance]);

  if (loading || isChecking) {
    // Show a loading spinner while checking user state
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <ProgressSpinner />
      </div>
    );
  }

  // Render children if not logged in
  return !currentUser ? <>{children}</> : null;
};

export default AuthRedirect;
